/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {ThunkAction} from "redux-thunk";
import BookingAction from "store/booking/BookingAction";
import {
    getAvailableDates,
    getCurrentDateIndex,
    getPossibleSlotIndex,
    getSelectedUpsellPackages,
    getTimeslotsAvailable,
} from "utils/packageUtils";
import {appendQueryParams, translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {RefObject, useEffect, useState} from "react";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {CURRENCY, HAS_HELP_ENABLED, USE_POSSIBLE_TIMESLOTS_ALLOWED} from "helpers/config";
import {BookingSteps} from "types/enums/BookingSteps";
import {RouteComponentProps} from "react-router-dom";
import {BookingTimeslotParams} from "pages/Front/Booking/BookingTimeslots";
import {getVoucherCodes} from "utils/voucherUtils";
import {useDebouncedDispatch} from "utils/useDebounceQueue";
import VoucherAction from "store/shop/VoucherAction";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";

const useCustomTimeslots = (
    setBookingStep: (bookingStep: BookingSteps) => void,
    history: RouteComponentProps["history"],
    match: RouteComponentProps<BookingTimeslotParams>["match"],
    scrollRefs: [RefObject<HTMLDivElement>, RefObject<HTMLDivElement>, RefObject<HTMLDivElement>] //* timeslots,customTimeslot, confirmTimeslots */
) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bookingTimeslots = useTypedSelector(state => state.bookingState.timeslots);
    const customSelectedTimeslotState = useTypedSelector(state => state.bookingState.bookingProcess.customTimeslots);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const hallState = useTypedSelector(state => state.hallState.hall);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const possibleTimeslots = useTypedSelector(state => state.bookingState.bookingProcess.possibleTimeslots);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const selectedTimeslotFromCart = packagesCart?.packagesCartItemSlots?.find((item, index) => index === 0);
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);

    const timeslots = bookingTimeslots?.map(bookingTimeslot => bookingTimeslot.timeslots) || [];
    // @ts-ignore
    const flattenedTimeslots: Timeslot[] = [].concat(...timeslots);
    const cartSelectedTimeslot = flattenedTimeslots.find(timeslot => timeslot.id === selectedTimeslotFromCart?.timeslot_id) || null;

    const [availableDates, setAvailableDates] = useState<Date[]>([]);
    const [customSelectedTimeslots, setCustomSelectedTimeslots] = useState<Timeslot[]>([]);
    const [firstPossibleSlotIndex, setFirstPossibleSlotIndex] = useState<number | null>(null);
    const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(true);
    const [isFetchingTimeslot, setIsFetchingTimeslot] = useState(false);
    const [lastPossibleSlotIndex, setLastPossibleSlotIndex] = useState<number | null>(null);
    const [selectCustomSlotsEnabled, setSelectCustomSlotsEnabled] = useState<boolean>(false);
    const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(cartSelectedTimeslot || null);
    const [stepsEnabled, setStepsEnabled] = useState<boolean>(false);
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const {debouncedDispatch} = useDebouncedDispatch();
    const {sendGenericEvent} = useReactGoogleAnalytics();
    const currentDateIndex = getCurrentDateIndex(availableDates, filter);
    const selectedUpsellPackages = getSelectedUpsellPackages(selectedUpsells);
    const timeslotsAvailable = getTimeslotsAvailable(flattenedTimeslots, hallState, selectedPackage);
    const {getExcludedDays} = useHallOpeningHours();

    const resetCustomSelectedTimeslotHandler = () => {
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        return dispatch(BookingAction.resetCustomTimeslot());
    };

    const setStepsEnabledHandler = (value: boolean) => {
        if (value === true) {
            resetCustomSelectedTimeslotHandler();
            setSelectedTimeslot(null);
            setPossibleTimeslotsHandler([]);
            setFirstPossibleSlotIndex(null);
            setLastPossibleSlotIndex(null);
            setCustomSelectedTimeslots([]);
            if (scrollRefs != null && scrollRefs.length > 0 && scrollRefs[0].current != null) {
                scrollRefs[0].current.scrollIntoView({behavior: "smooth"});
            }
        }
        setStepsEnabled(value);
    };
    const setPossibleTimeslotsHandler = (timeslots: Timeslot[]) => {
        return dispatch(BookingAction.setPossibleTimeslots(timeslots));
    };
    const setSelectCustomSlotsEnabledHandler = (enable: boolean) => {
        //provide general reset functionality if this is ever switched back
        resetCustomSelectedTimeslotHandler();
        setSelectCustomSlotsEnabled(enable);

        setSelectedTimeslot(null);
        setPossibleTimeslotsHandler([]);
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        setSelectedTimeslotHandler(selectedTimeslot, enable);
    };

    const possibleSlotIndex = (timeslot: Timeslot, type: "first" | "last") => {
        return getPossibleSlotIndex(timeslot, type, timeslotsAvailable, selectedPackage);
    };

    const setSelectedTimeslotHandler = (timeslot: Timeslot | null, selectCustomSlotsEnabled: boolean) => {
        toast.dismiss();
        if (!selectedPackage) {
            return;
        }

        if (!selectCustomSlotsEnabled) {
            if (USE_POSSIBLE_TIMESLOTS_ALLOWED) {
                fetchPossibleTimeslots(timeslot);
            }
            setSelectedTimeslot(timeslot);
            return;
        }

        if (!timeslot) {
            return;
        }
        const customSelectedTimeslotIndex = customSelectedTimeslotState.findIndex(customTimeslot => customTimeslot.id === timeslot.id);

        if (customSelectedTimeslotIndex !== -1 || (selectCustomSlotsEnabled && selectedPackage.typical_timeslot_count > customSelectedTimeslotState.length)) {
            return setCustomSelectedTimeslotHandler(timeslot);
        } else {
            toast.error(t("Too many slots"), {toastId: "tooManySlots"});
        }
        //     return;
        // }
        //
        // if (scrollRef != null && scrollRef.current != null) {
        //     //scrollRef.current.scrollIntoView({behavior: "smooth"});
        // }
    };

    const fetchPackagesOnDateChange = () => {
        const voucherCodes = getVoucherCodes(voucherItems);
        dispatch(VoucherAction.resetAppliedVouchers());
        debouncedDispatch(BookingAction.fetchPackagesByLocationParams(location, filter, true, voucherCodes));
    };
    const switchDate = date => {
        setSelectCustomSlotsEnabledHandler(false);
        dispatch(
            BookingAction.setFilter({
                ...filter,
                date: date,
            })
        );
        fetchPackagesOnDateChange();
    };

    const setCustomSelectedTimeslotHandler = (timeslot: Timeslot) => {
        return dispatch(BookingAction.setCustomTimeslot(timeslot));
    };

    const bookWithCustomTimeslot = () => {
        if (!packagesCart) {
            return;
        }

        if (
            !selectedPackage ||
            !customSelectedTimeslotState ||
            customSelectedTimeslotState.length === 0 ||
            customSelectedTimeslotState.length !== selectedPackage.typical_timeslot_count
        ) {
            return;
        }

        if (isFetchingTimeslot) {
            return;
        }
        const timeslotIds = customSelectedTimeslotState.map(timeslot => timeslot.id);
        setIsFetchingTimeslot(true);
        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchBookPackageWithCustomTimeslots(
                {
                    package_id: +match.params.package_id,
                    timeslot_ids: timeslotIds,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: selectedUpsellPackages,
                    hours_count: filter.hours_count ? filter.hours_count : 0,
                },
                packagesCart
            )
        ).then(r => {
            history.push("/booking/customer" + "?" + appendQueryParams(filter));
            setIsFetchingTimeslot(false);
        });

        setBookingStep(BookingSteps.Customer);
    };

    const sendGAData = () => {
        const count = filter.adults + filter.children;

        if (!selectedPackage) {
            return;
        }
        const value = selectedPackage.price * count;
        const item: DataLayerItem = {
            item_id: selectedPackage.package_id,
            item_name: translate(selectedPackage.name),
            quantity: count,
            item_variant: "",
            price: selectedPackage.price,
            currency: CURRENCY,
        };

        sendGenericEvent(GAEventName.AddToCart, value, [item]);
    };

    const bookWithAutomaticTimeslot = (timeslot: Timeslot) => {
        if (!packagesCart) {
            return;
        }

        if (isFetchingTimeslot) {
            return;
        }

        setIsFetchingTimeslot(true);
        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchBookPackage(
                {
                    package_id: +match.params.package_id,
                    timeslot_id: timeslot.id,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: selectedUpsellPackages,
                    hours_count: filter.hours_count ? filter.hours_count : 0,
                },
                packagesCart,
                "useCustomTimeslots - bookWithAutomaticTimeslot"
            )
        ).then(r => {
            history.push("/booking/customer" + "?" + appendQueryParams(filter));
            setIsFetchingTimeslot(false);
            sendGAData();
        });

        selectBookingStepHandler();
    };

    const fetchPossibleTimeslots = (timeslot: Timeslot | null) => {
        if (!timeslot) {
            //if I want to clear out the timeslots, reset everything
            return setPossibleTimeslotsHandler([]);
        }
        if (!packagesCart) {
            return;
        }
        if (selectedPackage && selectedPackage.category === PackageCategory.Event) {
            return;
        }

        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchPossibleTimeslots(
                {
                    package_id: +match.params.package_id,
                    timeslot_id: timeslot.id,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: selectedUpsellPackages,
                    hours_count: filter.hours_count ? filter.hours_count : 0,
                },
                packagesCart
            )
        ).then(r => {
            if ("timeslots" in r) {
                setPossibleTimeslotsHandler(r.timeslots);
            } else {
                setPossibleTimeslotsHandler([]);
            }
        });
    };

    const selectBookingStepHandler = () => {
        //setSelectedTimeslot(timeslot);
        setBookingStep(BookingSteps.Customer);
    };

    const fetchPackageTimeslots = () => {
        setIsLoadingTimeslots(true);
        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchPackagesTimeSlots({
                adults: filter.adults,
                children: filter.children,
                date: filter.date,
                package_id: +match.params.package_id,
                upsell: selectedUpsellPackages,
            })
        ).then(r => {
            setIsLoadingTimeslots(false);
            if (r.timeslotsAllowed === false) {
                //@todo refactor this into Package Submit
                // dispatch(BookingAction.fetchBookPackage({
                //     package_id: +props.match.params.package_id,
                //     adults: filter.adults,
                //     children: filter.children,
                //     upsell: [],
                // })).then(r => {
                //     consoleLog(r);
                //     props.history.push("/booking/customer");
                // });
            }
        });
    };

    useEffect(() => {
        if (selectedPackage) {
            setAvailableDates(getAvailableDates(selectedPackage, getExcludedDays()));
        }
    }, [filter.date]);

    useEffect(() => {
        setCustomSelectedTimeslots(customSelectedTimeslotState);
        if (customSelectedTimeslotState.length > 0) {
            setLastPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "first"));
        }
    }, [customSelectedTimeslots, customSelectedTimeslotState]);

    useEffect(() => {
        if (!packagesCart) {
            history.replace("/booking/package/" + match.params.package_id);
            return;
        }
        setIsLoadingTimeslots(true);
        const timedFetch = setTimeout(() => {
            fetchPackageTimeslots();
        }, 250);
        return () => {
            clearTimeout(timedFetch);
        };
    }, [match.params.package_id, filter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedPackage && selectedPackage.category !== PackageCategory.Event) {
                setStepsEnabled(HAS_HELP_ENABLED);
            }
        }, 500);
        return () => {
            setStepsEnabled(false);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (selectedTimeslot) {
            setLastPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "first"));
        }
    }, [timeslotsAvailable]);

    useEffect(() => {
        if (cartSelectedTimeslot && USE_POSSIBLE_TIMESLOTS_ALLOWED) {
            fetchPossibleTimeslots(cartSelectedTimeslot);
        }
    }, [cartSelectedTimeslot]);

    return {
        availableDates,
        bookWithAutomaticTimeslot,
        bookWithCustomTimeslot,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isLoadingTimeslots,
        isFetchingTimeslot,
        lastPossibleSlotIndex,
        possibleTimeslots,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
    };
};

export default useCustomTimeslots;
